/**
 * Team (QM-A12-001/003/004, QM-D05-002) via /v1/team/*: members + pending invites + seats, invite by email and role,
 * role change, suspend / remove with a reason, reactivate, revoke invite. Role and status values are translated.
 * The server enforces permissions (manage_team), self-escalation and last-owner rules; the screen only reflects them.
 */
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, ErrorState, Heading, Loading, Panel, Text } from '@qm/ui';
import { InvitableRoleSchema, TEAM_ROLES } from '@qm/contracts/team';
import { useApp } from '../../../lib/app-context';
import { BffError, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';

interface Member { id: string; email?: string; displayName?: string | null; name?: string | null; role?: string; teamRole?: string; status: string; userId?: string }
interface Invite { id: string; email: string; team_role: string; expires_at: string }
interface TeamData { members: Member[]; invites: Invite[]; seats?: { used: number; limit: number } }

const roleOf = (m: Member) => m.teamRole ?? m.role ?? 'member';

export function TeamPage() {
  const { t, me } = useApp();
  const f = useFormat();
  const res = useResource<TeamData>('/v1/team/members');
  const perms = useResource<{ permissions: string[] }>('/v1/team/me/permissions');
  const canManage = perms.status === 'ready' && perms.data.permissions.includes('manage_team');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('member');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const roleLabel = (r: string) => t(`shell.team.role.${r}`);
  const statusLabel = (s: string) => t(`shell.team.status.${s}`);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMessage(null);
    try { await fn(); setMessage(t(ok)); res.reload(); } catch (e) { setMessage(errorText(t, e)); } finally { setBusy(false); }
  };
  const invite = (e: FormEvent) => {
    e.preventDefault();
    if (!email) return;
    void run(() => bffJson('/v1/team/invites', 'POST', { email, role }), 'shell.team.invited').then(() => setEmail(''));
  };
  const withReason = (id: string, action: 'suspend' | 'remove') => {
    const reason = window.prompt(t('shell.team.reasonPrompt')) ?? '';
    if (reason.trim().length < 5) { setMessage(t('shell.team.reasonRequired')); return; }
    void run(() => bffJson(`/v1/team/members/${encodeURIComponent(id)}/${action}`, 'POST', { reason: reason.trim() }), action === 'suspend' ? 'shell.team.suspended' : 'shell.team.removed');
  };

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <div className="qm-page__head">
        <div>
          <Heading level={1} id="page-title">{t('shell.screens.team.title')}</Heading>
          <Text tone="muted">{t('shell.screens.team.intro')}</Text>
        </div>
      </div>
      <p role="status" aria-live="polite" data-testid="team-message">{message ?? ''}</p>
      {canManage ? (
        <Panel>
          <form onSubmit={invite} className="qm-form" data-testid="team-invite">
            <Heading level={2}>{t('shell.team.invite')}</Heading>
            <label className="qm-field"><span className="qm-field__label">{t('shell.team.email')}</span>
              <input className="qm-input" type="email" autoComplete="off" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label className="qm-field"><span className="qm-field__label">{t('shell.col.role')}</span>
              <select className="qm-input" value={role} onChange={(e) => setRole(e.target.value)}>
                {InvitableRoleSchema.options.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
              </select></label>
            <Button type="submit" variant="primary" disabled={busy || !email}>{t('shell.team.sendInvite')}</Button>
          </form>
        </Panel>
      ) : null}
      {res.status === 'loading' ? <Loading label={t('common.loading')} /> : res.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : (
        <>
          {res.data.seats ? <Text tone="muted" data-testid="team-seats">{t('shell.team.seats', { used: res.data.seats.used, limit: res.data.seats.limit })}</Text> : null}
          {res.data.members.length === 0 ? <EmptyState title={t('shell.screens.team.empty')} /> : (
            <table className="qm-table" data-testid="team-members">
              <caption className="qm-sr-only">{t('shell.screens.team.title')}</caption>
              <thead><tr><th scope="col">{t('shell.col.name')}</th><th scope="col">{t('shell.col.role')}</th><th scope="col">{t('shell.col.status')}</th>{canManage ? <th scope="col">{t('shell.team.actions')}</th> : null}</tr></thead>
              <tbody>
                {res.data.members.map((m) => {
                  const self = m.userId === me?.userId;
                  return (
                    <tr key={m.id} data-member={m.id}>
                      <td data-label={t('shell.col.name')}><bdi>{f.text(m.displayName ?? m.name ?? m.email ?? '')}</bdi></td>
                      <td data-label={t('shell.col.role')}>
                        {canManage && !self && m.status === 'active' ? (
                          <select className="qm-input" aria-label={t('shell.team.changeRole', { name: m.email ?? m.id })} value={roleOf(m)} disabled={busy}
                            onChange={(e) => void run(() => bffJson(`/v1/team/members/${encodeURIComponent(m.id)}/role`, 'PUT', { role: e.target.value }), 'shell.team.roleChanged')}>
                            {TEAM_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                          </select>
                        ) : roleLabel(roleOf(m))}
                      </td>
                      <td data-label={t('shell.col.status')}>{statusLabel(m.status)}</td>
                      {canManage ? (
                        <td data-label={t('shell.team.actions')}>
                          {!self && m.status === 'active' ? <><Button onClick={() => withReason(m.id, 'suspend')} disabled={busy}>{t('shell.team.suspend')}</Button>{' '}<Button onClick={() => withReason(m.id, 'remove')} disabled={busy}>{t('shell.team.remove')}</Button></> : null}
                          {m.status === 'suspended' ? <Button onClick={() => void run(() => bffJson(`/v1/team/members/${encodeURIComponent(m.id)}/reactivate`, 'POST', {}), 'shell.team.reactivated')} disabled={busy}>{t('shell.team.reactivate')}</Button> : null}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {res.data.invites.length ? (
            <>
              <Heading level={2}>{t('shell.team.pending')}</Heading>
              <ul className="qm-list" data-testid="team-invites">
                {res.data.invites.map((i) => (
                  <li key={i.id}><bdi>{i.email}</bdi> · {roleLabel(i.team_role)} · {t('shell.team.expires', { date: f.date(i.expires_at) })}
                    {canManage ? <> <Button onClick={() => void run(() => bffJson(`/v1/team/invites/${encodeURIComponent(i.id)}/revoke`, 'POST', {}), 'shell.team.revoked')} disabled={busy}>{t('shell.team.revoke')}</Button></> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

export function errorText(t: (k: string) => string, e: unknown): string {
  const details = e instanceof BffError ? (e.envelope?.error.details as { reason?: string } | undefined) : undefined;
  if (details?.reason) { const k = `shell.team.error.${details.reason}`; const m = t(k); if (m !== k) return m; }
  return t('shell.team.error.generic');
}
